
import React from "react";
import { Modal, Badge } from "@/components/ui";
import { fmtDate, fmt } from "@/lib/formatters";
import { Package, Truck, User, ArrowLeftCircle, CheckCircle, FileText, Calendar, DollarSign, AlertTriangle } from "lucide-react";

export const MaterialReturnDetailModal = ({ selReturn, onClose, user, onConfirmReceipt }) => {
  if (!selReturn) return null;

  const isInventoryManager =
    user?.role === 'store_incharge' ||
    user?.role === 'store_incharge' ||
    user?.role === "admin" ||
    user?.role === "verve-admin" ||
    user?.role === "accountant" ||
    user?.role === "owner";

  const data = selReturn.originalData || selReturn;

  // Calculate summary metrics
  let totalQty = 0;
  let totalDamagedQty = 0;
  let itemsWithDamage = 0;
  let totalValue = 0;

  // Parse damage count from notes text if no structured data or if structured data lacks quantities
  const notes = data.originalData?.notes || data.notes || '';
  let parsedDamagedCount = 0;
  if (notes.includes('Damage Report:')) {
      const damageMatch = notes.match(/Damage Report:\s*(\d+)\s*item\(s\)\s*damaged/i);
      if (damageMatch) {
          parsedDamagedCount = parseInt(damageMatch[1]);
      }
  }

  (data.items || []).forEach((it, idx) => {
    const item = it.inventoryId?.item || it.item || {};
    const unitPrice = it.inventoryId?.purchaseRate || it.inventoryId?.unitPrice || item.purchaseAmount || item.unitPrice || it.unitPrice || it.rate || 0;
    const qty = it.quantity || it.qty || 0;
    
    const damageInfo = data.damageReport?.find(dr => 
       (dr.item?._id || dr.item)?.toString() === (it.inventoryId?._id || it.inventoryId)?.toString() ||
       (dr.item?._id || dr.item)?.toString() === (item?._id || item)?.toString()
    );
    
    let dmgQty = 0;
    if (data.rawType?.toLowerCase() === 'damage' || data.returnType?.toLowerCase() === 'damage') {
        dmgQty = qty;
    } else {
        dmgQty = damageInfo?.damagedQty || it.damagedQty || 0;
        if (!dmgQty && parsedDamagedCount > 0) {
            if (damageInfo) {
                dmgQty = parsedDamagedCount;
            } else if (idx === 0) {
                dmgQty = parsedDamagedCount;
            }
        }
    }

    totalQty += Number(qty);
    totalDamagedQty += Number(dmgQty);
    if (Number(dmgQty) > 0) itemsWithDamage += 1;
    totalValue += (Number(unitPrice) * Number(qty));
  });

  const goodQty = totalQty - totalDamagedQty;
  
  // Extract real damage reasons instead of generic backend notes
  let damageNotes = data.damageReport?.filter(dr => dr.reason && dr.reason !== 'Not specified').map(dr => dr.reason).join("; ");
  if (!damageNotes && data.notes) {
      // Strip generic backend append
      damageNotes = data.notes.replace(/Damage Report:\s*\d+\s*item\(s\)\s*damaged\.?/gi, '').trim();
      if (!damageNotes && parsedDamagedCount > 0) {
         damageNotes = `Damage reported during return processing`;
      }
  }
  if (!damageNotes && totalDamagedQty > 0) damageNotes = `Damage Report: ${itemsWithDamage} item(s) damaged.`;

  return (
    <Modal
      isOpen={!!selReturn}
      onClose={onClose}
      title="Material Return Details"
    >
      <div className="space-y-6">
        {/* Header Information */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-4">
             <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center border border-slate-100">
                <ArrowLeftCircle size={24} />
             </div>
             <div>
                <h3 className="text-[16px] font-semibold text-slate-800 font-['Poppins']">Site Return Request</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[12px] font-medium text-slate-500">
                    ID: {data.returnId || `RET-${(data._id || '').toString().slice(-6).toUpperCase()}`}
                  </span>
                  <span className="text-slate-300">•</span>
                  <span className="text-[11px] font-medium text-slate-400">
                    Created {fmtDate(data.createdAt)}
                  </span>
                </div>
             </div>
          </div>
          <Badge status={selReturn.status || data.status || 'PENDING'} />
        </div>

        {/* Return Information Grid */}
        <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <Calendar size={14} />
                <span className="text-[11px] font-semibold uppercase tracking-wider">Return Info</span>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Return Date</p>
                <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">{fmtDate(selReturn.date || data.returnDate || data.date || data.createdAt) || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Reason</p>
                <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">{data.reason || data.notes || data.remarks || 'General Return'}</p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <DollarSign size={14} />
                <span className="text-[11px] font-semibold uppercase tracking-wider">Financials</span>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Estimated Value</p>
                <p className="text-[13px] font-semibold text-[#2b448f] font-['Poppins']">
                  {fmt(data.totalAmount || data.amount || (data.items || []).reduce((sum, it) => {
                    const item = it.inventoryId?.item || it.item || {};
                    const rate = it.inventoryId?.purchaseRate || it.inventoryId?.unitPrice || item.purchaseAmount || item.unitPrice || it.unitPrice || it.rate || 0;
                    return sum + (rate * (it.quantity || it.qty || 0));
                  }, 0))}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Total Items</p>
                <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">{data.items?.length || 0} items</p>
              </div>
            </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
           <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <Truck size={14} />
                <span className="text-[11px] font-semibold uppercase tracking-wider">Logistics</span>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Driver / Vehicle</p>
                <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">
                  {data.driverName || '—'} {data.vehicleNumber ? `(${data.vehicleNumber})` : ''}
                </p>
              </div>
           </div>

           <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <User size={14} />
                <span className="text-[11px] font-semibold uppercase tracking-wider">Reporting</span>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-medium uppercase">Project ID</p>
                <p className="text-[13px] font-semibold text-[#f97316] font-['Poppins']">
                  {data.project?.projectId || data.projectId || 'N/A'}
                </p>
              </div>
           </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-[14px] font-semibold text-slate-800 flex items-center gap-2 font-['Poppins']">
            <Package size={18} className="text-slate-400" />
            Returned Materials
          </h4>
          <div className="border border-slate-100 rounded-xl overflow-hidden bg-white shadow-sm">
             <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase">Item Details</th>
                    <th className="px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase text-center">Total Qty</th>
                    <th className="px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase text-center">Good / Damaged</th>
                    <th className="px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase text-right">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {data.items?.map((it, idx) => {
                      const item = it.inventoryId?.item || it.item || {};
                      const name = item.itemName || item.name || it.itemName || it.name || 'Unknown Item';
                      const sku = item.sku || item.itemCode || it.itemCode || 'N/A';
                      // Robust price lookup: check inventory record rate, item master price, or return entry rate
                      const unitPrice = it.inventoryId?.purchaseRate || it.inventoryId?.unitPrice || item.purchaseAmount || item.unitPrice || it.unitPrice || it.rate || 0;
                      const totalQty = it.quantity || it.qty || 0;
                      const totalValue = unitPrice * totalQty;
                      
                      const damageInfo = data.damageReport?.find(dr => 
                         (dr.item?._id || dr.item)?.toString() === (it.inventoryId?._id || it.inventoryId)?.toString() ||
                         (dr.item?._id || dr.item)?.toString() === (item?._id || item)?.toString()
                      );
                      let damagedQty = (data.rawType?.toLowerCase() === 'damage' || data.returnType?.toLowerCase() === 'damage') ? totalQty : (damageInfo?.damagedQty || it.damagedQty || 0);
                      
                      if (!damagedQty && parsedDamagedCount > 0) {
                          if (damageInfo) {
                              damagedQty = parsedDamagedCount;
                          } else if (idx === 0) {
                              damagedQty = parsedDamagedCount;
                          }
                      }
                      
                      const goodQty = totalQty - damagedQty;
                      
                      return (
                       <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3">
                           <div>
                             <p className="text-[13px] font-medium text-slate-700 font-['Poppins']">{name}</p>
                             <p className="text-[10px] text-slate-400 uppercase tracking-tight">{sku}</p>
                           </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                           <span className="text-[13px] font-medium text-slate-600">{totalQty}</span>
                           <span className="ml-1 text-[10px] text-slate-400 uppercase">{item.units?.unitName || item.unit || it.unit || 'pcs'}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                           <div className="flex items-center justify-center gap-2">
                             <span className="text-[12px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">{goodQty} Good</span>
                             {damagedQty > 0 && (
                               <span className="text-[12px] font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">{damagedQty} Dmg</span>
                             )}
                           </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                           <span className="text-[13px] font-semibold text-[#2b448f] font-['Poppins']">{fmt(totalValue)}</span>
                        </td>
                      </tr>
                     );
                   })}
                </tbody>
             </table>
          </div>
        </div>

        {/* Damaged Items Section (Only show if there are damaged items) */}
        {totalDamagedQty > 0 && (
          <div className="space-y-4 pt-2">
            <h4 className="text-[15px] font-bold text-red-600 flex items-center gap-2 font-['Poppins']">
              <AlertTriangle size={18} />
              Damaged Items Information
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
               <div className="p-4 rounded-xl border border-red-50 bg-white shadow-[0_2px_10px_-4px_rgba(239,68,68,0.1)]">
                 <p className="text-[11px] text-red-400 font-bold uppercase tracking-wider mb-2">Total Damaged Quantity</p>
                 <p className="text-3xl font-bold text-red-600 font-['Poppins']">{totalDamagedQty}</p>
               </div>
               <div className="p-4 rounded-xl border border-red-50 bg-white shadow-[0_2px_10px_-4px_rgba(239,68,68,0.1)]">
                 <p className="text-[11px] text-red-400 font-bold uppercase tracking-wider mb-2">Items With Damage</p>
                 <p className="text-3xl font-bold text-red-600 font-['Poppins']">{itemsWithDamage}</p>
               </div>
            </div>

            <div className="p-4 rounded-xl border border-red-50 bg-white shadow-[0_2px_10px_-4px_rgba(239,68,68,0.1)] space-y-2">
               <h5 className="text-[11px] font-bold text-red-600 uppercase tracking-wider flex items-center gap-2">
                 <AlertTriangle size={14} /> Damage Report Notes
               </h5>
               <p className="text-[13px] text-slate-600 italic">"{damageNotes}"</p>
            </div>
          </div>
        )}

        {/* Return Summary */}
        <div className="p-5 rounded-2xl border border-slate-100 bg-white shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] mt-4">
           <h4 className="text-[16px] font-bold text-slate-800 flex items-center gap-2 font-['Poppins'] mb-4">
             <FileText size={20} className="text-slate-700" />
             Return Summary
           </h4>
           <div className="grid grid-cols-4 gap-4 divide-x divide-slate-100">
              <div className="text-center">
                 <p className="text-2xl font-bold text-[#f97316] font-['Poppins']">{totalQty}</p>
                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">Total Qty</p>
              </div>
              <div className="text-center">
                 <p className="text-2xl font-bold text-emerald-600 font-['Poppins']">{goodQty}</p>
                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">Good Qty</p>
              </div>
              <div className="text-center">
                 <p className="text-2xl font-bold text-red-600 font-['Poppins']">{totalDamagedQty}</p>
                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">Damaged Qty</p>
              </div>
              <div className="text-center pl-2">
                 <p className="text-2xl font-bold text-[#8b5cf6] font-['Poppins']">{fmt(totalValue)}</p>
                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">Total Value</p>
              </div>
           </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 pt-4 border-t border-slate-100">
           <button 
             className="flex-1 px-4 py-2.5 bg-slate-50 text-slate-600 font-medium text-[13px] rounded-lg border border-slate-200 hover:bg-slate-100 transition-all" 
             onClick={onClose}
           >
             Close
           </button>

        </div>
      </div>
    </Modal>
  );
};

